import { Modal, Text, TextField } from "@shopify/polaris"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import stores from "@/stores"
import { handleRenderModal } from "@/utils/renderModal"
import toast from "@/utils/toast"

import Api from "../../Api"

interface LeaveContactModalProps {
  open: boolean
  onClose: () => void
  loading?: boolean
}

export function LeaveContactModal({
  open,
  onClose,
  loading = false,
}: LeaveContactModalProps) {
  const { t } = useTranslation("delivery_guarantee")
  const { infoData } = stores.DeliveryGuarantee
  const [submiting, setSubmiting] = useState(false)
  const [email, setEmail] = useState(infoData.customEmail)
  const [emailError, setEmailError] = useState("")

  const handleEmailChange = (value: string) => {
    setEmail(value)
    setEmailError("")
  }

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

    return emailRegex.test(email)
  }

  const handleSubmit = async () => {
    if (!email.trim()) {
      setEmailError(t("Sett.ContactEmailRequired"))
      return
    }

    if (!validateEmail(email)) {
      setEmailError(t("Sett.ContactEmailInvalid"))
      return
    }
    setSubmiting(true)
    const res = await Api.setPersonalSettings({ 
      customEmail: email, 
    })
    
    if (res.data.code === 200) {
      stores.DeliveryGuarantee.getInfo()
      toast(t("toast.SavedSuccess", { ns: "delivery_guarantee" }))
      onClose()
    }
    setSubmiting(false)
  }

  const handleClose = () => {
    setEmail("")
    setEmailError("")
    onClose()
  }

  const isSubmitDisabled = !email.trim() || loading

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={t("Sett.LeaveDedicatedProtectionContact")}
      primaryAction={{
        content: t("common:Submit") as string,
        onAction: handleSubmit,
        loading: submiting,
        disabled: isSubmitDisabled,
      }}
      secondaryActions={[
        {
          content: t("common:Cancel") as string,
          onAction: handleClose,
        },
      ]}
    >
      <Modal.Section>
        <div className="mb-3">
          <Text as="p" variant="bodyMd">
            {t("Sett.LeaveDedicatedProtectionContactDes1")}
          </Text>
          <Text as="p" variant="bodyMd">
            {t("Sett.LeaveDedicatedProtectionContactDes2")}
          </Text>
        </div>
        
        <TextField
          label={t("Sett.ContactEmail")}
          value={email}
          onChange={handleEmailChange}
          placeholder={t("Sett.ContactEmailPlaceholder")}
          error={emailError}
          autoComplete="email"
          type="email"
        />
      </Modal.Section>
    </Modal>
  )
}

export default LeaveContactModal

// eslint-disable-next-line react-refresh/only-export-components
export const useLeaveContactModal = handleRenderModal(LeaveContactModal)
